import { z } from "zod";

import { ProjectLanguage } from "./enums.js";
import { notNullish } from "./filterGuards.js";
import { StripeMeterId, StripePriceId } from "./stripeIdTypeBrands.js";
import { KernelImage } from "./typeBrands.js";
import { typedObjectEntries, typedObjectKeys } from "./utils/typedObjects.js";

import { zBrandedRecord, zKeyedRecord } from "./zod/utils.js";
export const CUSTOMER_EVAL_THREAD_IDLE_TIMEOUT_MS = 5 * 60 * 1_000; // 5 minutes
export const SIGNED_EMBEDDED_KERNEL_IDLE_TIMEOUT_MS = 5 * 60 * 1_000; // 5 minutes
export const EXPLORE_IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export const DEFAULT_KERNEL_SIZE = "medium";

export const StandardKernelOptionsUnionLiteral = z.enum([
  "xsmall",
  "small",
  "medium",
]);

// Keep this list in sync with kernelSizeSchemaV1 in
// packages/server/src/rpc/procedures/project/batchUpdateComputeProfileProcedure.ts,
// which mirrors these literals in a zod enum so the public API's OpenAPI spec
// advertises the accepted values.
const KernelOptionsUnionLiteral = z.union([
  StandardKernelOptionsUnionLiteral,
  z.literal("large"),
  z.literal("xlarge"),
  z.literal("2xlarge"),
  z.literal("4xlarge"),
  z.literal("l4"),
  z.literal("a10g"),
]);

export const KernelSize = KernelOptionsUnionLiteral;
export type KernelSize = z.infer<typeof KernelOptionsUnionLiteral>;

export const KernelResourceConfig = z.object({
  cpu: z.string(),
  memory: z.string(),
  "nvidia.com/gpu": z.string().optional(),
});
export type KernelResourceConfig = z.infer<typeof KernelResourceConfig>;

const KernelSizeConfig = z.object({
  humanName: z.string(),
  computePool: z.string().nullable().optional(),
  resources: z.object({
    limits: KernelResourceConfig,
    requests: KernelResourceConfig,
  }),
  nodeGroup: z.string().nullable().optional(),
});

const KernelImageReplicas = zKeyedRecord(KernelSize, z.number().or(z.null()));
type KernelImageReplicas = z.infer<typeof KernelImageReplicas>;

const KernelImageConfig = z.object({
  name: z.string(),
  priority: z.number().optional(),
  imageUrl: z.string().nullable().optional(),
  replicas: KernelImageReplicas,
});
type KernelImageConfig = z.infer<typeof KernelImageConfig>;

const KernelImages = zBrandedRecord<KernelImage, typeof KernelImageConfig>(
  KernelImageConfig,
);
export type KernelImages = z.infer<typeof KernelImages>;

export const KernelConfig = z.object({
  tag: z.string(),
  image: z.string(),
  defaultBaseImage: KernelImage,
  baseImages: KernelImages,
});
export type KernelConfig = z.infer<typeof KernelConfig>;

export const KernelSizes = zKeyedRecord(KernelSize, KernelSizeConfig);
export type KernelSizes = z.infer<typeof KernelSizes>;

export const KernelManagerConfig = z
  .object({
    backendHostname: z.string(),
    publicKernelSize: KernelSize,
    kernelSizes: KernelSizes,
    pythonKernel: KernelConfig,
    rKernel: KernelConfig,
  })
  // `superRefine`, not `refine`: `validateKernelConfig` returns its failure
  // reason as a string, which `.refine` reads as `true` -- the check would not
  // run at all. `superRefine` carries the reason through as the issue message.
  .superRefine(({ kernelSizes, pythonKernel, rKernel }, ctx) => {
    const reason =
      validateKernelConfig(rKernel, kernelSizes) ??
      validateKernelConfig(pythonKernel, kernelSizes);
    if (reason != null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: reason });
    }
  });

export type KernelManagerConfig = z.infer<typeof KernelManagerConfig>;

function validateKernelConfig(
  kernelConfig: KernelConfig,
  kernelSizes: KernelSizes,
): string | undefined {
  if (!(kernelConfig.defaultBaseImage in kernelConfig.baseImages)) {
    return `Default base image ${kernelConfig.defaultBaseImage} not found in base images`;
  }
  const failures = typedObjectEntries(kernelConfig.baseImages)
    .map(([baseImage, baseImageConfig]) => {
      const imageReplicas = typedObjectKeys(baseImageConfig.replicas);
      if (imageReplicas.length !== Object.keys(kernelSizes).length) {
        return `Base image ${baseImage} does not specify replicas for all kernel sizes`;
      }
      imageReplicas.forEach((kernelSize) => {
        if (!(kernelSize in kernelSizes)) {
          return `Base image ${baseImage} specifies replicas for invalid kernel size ${kernelSize}`;
        }
      });
    })
    .filter(notNullish);

  if (failures.length > 1) {
    return failures.join(".\n");
  }
}

/**
 * Returns the base kernel images for the given project language.
 */
export function getBaseImagesForProjectLanguage(
  kernelManagerConfig: KernelManagerConfig,
  projectLanguage: ProjectLanguage,
): KernelImages {
  return projectLanguage === ProjectLanguage.R
    ? kernelManagerConfig.rKernel.baseImages
    : kernelManagerConfig.pythonKernel.baseImages;
}

interface BillingConfig {
  priceId: StripePriceId | null;
  eventName: string;
  meterId: StripeMeterId | null;
}
export type ComputeProfileBillingConfig = Record<KernelSize, BillingConfig>;
